import dayjs from "dayjs"
import moment from "moment-timezone"
import { download } from "./utils"
import { createLetterPdf } from "./pdfUtils"
import type { IDeclarationsReportSwimmer } from "./MeetActionsModal" // temporarily import type; can be moved if needed

export type DeclarationsStatus = "attending" | "notAttending" | "noResponse"

type RelayGenderFilter = "both" | "girls" | "boys"

const statusRank: Record<DeclarationsStatus, number> = {
  attending: 0,
  notAttending: 1,
  noResponse: 2,
}

export const formatSwimmerNumber = (num: number | null | undefined): string => {
  if (num === null || num === undefined) return ""
  return num.toString().padStart(3, "0")
}

export const deriveDeclarationStatus = (
  swimmer: IDeclarationsReportSwimmer,
): DeclarationsStatus => {
  if (swimmer.confirmedForMeet === 1) return "attending"
  if (swimmer.removedFromMeet === 1) return "notAttending"
  return "noResponse"
}

export const compareDeclarationsByStatusThenName = (
  a: IDeclarationsReportSwimmer,
  b: IDeclarationsReportSwimmer,
): number => {
  const statusA = deriveDeclarationStatus(a)
  const statusB = deriveDeclarationStatus(b)

  const rankDiff = statusRank[statusA] - statusRank[statusB]
  if (rankDiff !== 0) return rankDiff

  const lastA = (a.last_name || "").toLowerCase()
  const lastB = (b.last_name || "").toLowerCase()
  if (lastA < lastB) return -1
  if (lastA > lastB) return 1

  const firstA = (a.first_name || "").toLowerCase()
  const firstB = (b.first_name || "").toLowerCase()
  if (firstA < firstB) return -1
  if (firstA > firstB) return 1

  return 0
}

export const formatDeclarationAudit = (
  s: IDeclarationsReportSwimmer,
): string | null => {
  if (!s.lastDeclarationAt) return null

  const whoParts: string[] = []
  if (s.lastDeclarationActorName) {
    whoParts.push(s.lastDeclarationActorName)
  }
  if (s.lastDeclarationActorEmail) {
    whoParts.push(s.lastDeclarationActorEmail)
  }

  const when = dayjs(s.lastDeclarationAt).local().format("M/D/YYYY h:mm A")
  const roleLabel = s.lastDeclarationActorIsParent ? "parent" : "admin"

  if (whoParts.length > 0) {
    return `${when} by ${whoParts.join(" ")} (${roleLabel})`
  }
  return `${when} (${roleLabel})`
}

export const getPdfAuditLines = (
  s: IDeclarationsReportSwimmer,
): { whenLine: string; nameLine: string | null; emailRoleLine: string | null } | null => {
  if (!s.lastDeclarationAt) return null

  const whenLine = dayjs(s.lastDeclarationAt).local().format("M/D/YYYY h:mm A")

  const nameLine = s.lastDeclarationActorName || null

  let emailRoleLine: string | null = null
  if (s.lastDeclarationActorEmail) {
    const roleLabel = s.lastDeclarationActorIsParent ? "parent" : "admin"
    emailRoleLine = `${s.lastDeclarationActorEmail} (${roleLabel})`
  } else if (
    s.lastDeclarationActorIsParent !== null &&
    s.lastDeclarationActorIsParent !== undefined
  ) {
    const roleLabel = s.lastDeclarationActorIsParent ? "parent" : "admin"
    emailRoleLine = `(${roleLabel})`
  }

  return { whenLine, nameLine, emailRoleLine }
}

export const buildDeclarationsCsv = (
  swimmers: IDeclarationsReportSwimmer[],
  options: {
    meetName: string
    dateLabel: string
    clubLabel: string
    courseLabel: string
  },
): string => {
  const { meetName, dateLabel, clubLabel, courseLabel } = options

  const detailsLines = [
    ["Meet Name", meetName],
    ["Meet Date", dateLabel],
    ["Club", clubLabel],
    ["Course", courseLabel],
    ["Generated At", moment().format("YYYY-MM-DD HH:mm")],
  ].map((pair) =>
    pair
      .map((val) => (typeof val === "string" ? `"${val.replace(/"/g, '""')}"` : String(val)))
      .join(","),
  )

  const swimmersSorted = [...swimmers].sort(compareDeclarationsByStatusThenName)

  const rows = swimmersSorted.map((s) => {
    const status = deriveDeclarationStatus(s)
    const eventsCount =
      s.elligibleEvents?.filter(
        (evt) => evt.isRelay !== 1 && evt.enteredInEvent === 1,
      ).length || 0
    const audit = formatDeclarationAudit(s)
    const relayEvents =
      s.elligibleEvents?.filter((evt) => evt.isRelay === 1) || []

    const relaySummaries = relayEvents
      .filter((evt) => evt.relayAvailable === 1 || evt.relayAvailable === 0)
      .map((evt) => {
        const relayLabel = `#${evt.eventNumber} ${evt.age_group} ${evt.eventGender} ${evt.distance} ${evt.stroke}`
        const statusLabel = evt.relayAvailable === 1 ? "Available" : "Unavailable"
        return `${relayLabel}: ${statusLabel}`
      })

    const relaySummary = relaySummaries.join("; ")
    return {
      swimmerNumber: formatSwimmerNumber(s.swimmerNumber ?? null),
      firstName: s.first_name,
      lastName: s.last_name,
      gender: s.gender,
      leagueAge: s.leagueAge,
      status,
      individualEventsEntered: eventsCount,
      relayAvailability: relaySummary,
      comments: s.comments || "",
      lastUpdated: audit || "",
    }
  })

  const header = [
    "Swimmer Number",
    "First Name",
    "Last Name",
    "Gender",
    "League Age",
    "Status",
    "Individual Events Entered",
    "Relay Availability",
    "Parent Comments",
    "Last Updated",
  ]

  const dataLines = rows.map((r) =>
    [
      r.swimmerNumber,
      r.firstName,
      r.lastName,
      r.gender,
      r.leagueAge,
      r.status,
      r.individualEventsEntered,
      r.relayAvailability,
      r.comments,
      r.lastUpdated,
    ]
      .map((val) => (typeof val === "string" ? `"${val.replace(/"/g, '""')}"` : String(val)))
      .join(","),
  )

  const csvLines = [
    ...detailsLines,
    "",
    header.join(","),
    ...dataLines,
  ]

  return csvLines.join("\n")
}

export const downloadDeclarationsCsv = (
  swimmers: IDeclarationsReportSwimmer[],
  options: {
    meetName: string
    dateLabel: string
    clubLabel: string
    courseLabel: string
  },
) => {
  const csv = buildDeclarationsCsv(swimmers, options)
  download(csv, "meet_declarations_report.csv", "text/csv;charset=utf-8;")
}

export const buildDeclarationsPdf = (
  swimmers: IDeclarationsReportSwimmer[],
  options: {
    meetName: string
    dateLabel: string
    clubLabel?: string
    courseLabel: string
  },
) => {
  const { meetName, dateLabel, clubLabel, courseLabel } = options
  const doc = createLetterPdf()

  doc.setFontSize(12)
  let y = 10
  doc.text(`Meet Name: ${meetName}`, 10, y)
  y += 6
  doc.text(`Meet Date: ${dateLabel}`, 10, y)
  y += 6
  if (clubLabel) {
    doc.text(`Club: ${clubLabel}`, 10, y)
    y += 6
  }
  doc.text(`Course: ${courseLabel}`, 10, y)
  y += 6
  doc.text(`Generated At: ${moment().format("YYYY-MM-DD HH:mm")}`, 10, y)

  const headerLabels = [
    "#",
    "Last, First",
    "Gender",
    "Age",
    "Ind. Events",
    "Parent Comments",
    "Last Updated",
  ]
  doc.setFontSize(8)
  const pageWidth = doc.internal.pageSize.getWidth()
  const rightMargin = 10
  const maxRight = pageWidth - rightMargin

  const tableLeft = 10
  const tableRight = maxRight
  const tableWidth = tableRight - tableLeft

  // Relative widths for each column: must sum to 1
  const colRelative = [0.08, 0.24, 0.07, 0.08, 0.12, 0.26, 0.15]
  const colWidths = colRelative.map((r) => tableWidth * r)

  const colBorders: number[] = [tableLeft]
  for (let i = 0; i < colWidths.length; i++) {
    colBorders.push(colBorders[i] + colWidths[i])
  }

  const bodyFontSize = 8
  const lineHeight = 4
  const cellPaddingY = 2

  const maxY = 280
  const startY = y + 10
  let rowY = startY

  const drawSectionHeader = (label: string) => {
    doc.setFontSize(10)
    doc.text(label, 10, rowY)
    rowY += 5
  }

  const drawTableHeader = () => {
    doc.setFontSize(bodyFontSize)
    const headerTop = rowY
    const headerHeight = cellPaddingY * 2 + lineHeight

    headerLabels.forEach((label, idx) => {
      const maxTextWidth = colWidths[idx] - 4
      const textLines = doc.splitTextToSize(label, maxTextWidth)
      let textY = headerTop + cellPaddingY + lineHeight
      textLines.forEach((text: string) => {
        doc.text(text, colBorders[idx] + 2, textY)
        textY += lineHeight
      })
    })

    // Draw header grid
    doc.rect(tableLeft, headerTop, tableWidth, headerHeight)
    for (let i = 1; i < colBorders.length; i++) {
      doc.line(colBorders[i], headerTop, colBorders[i], headerTop + headerHeight)
    }

    rowY += headerHeight
  }

  type SectionConfig = {
    status: DeclarationsStatus
    label: string
  }

  const sections: SectionConfig[] = [
    { status: "attending", label: "Attending" },
    { status: "notAttending", label: "Not Attending" },
    { status: "noResponse", label: "No Response" },
  ]

  sections.forEach((section, index) => {
    const swimmersInSection = swimmers
      .filter((s) => deriveDeclarationStatus(s) === section.status)
      .sort((a, b) => compareDeclarationsByStatusThenName(a, b))

    if (swimmersInSection.length === 0) {
      return
    }

    const currentSectionLabel = section.label

    if (index > 0) {
      rowY += 4
    }

    if (rowY + 15 > maxY) {
      doc.addPage()
      rowY = 10
    }

    drawSectionHeader(currentSectionLabel)
    drawTableHeader()

    swimmersInSection.forEach((s) => {
      const eventsCount =
        s.elligibleEvents?.filter(
          (evt) => evt.isRelay !== 1 && evt.enteredInEvent === 1,
        ).length || 0

      const auditLines = getPdfAuditLines(s)

      const comments = (s.comments || "").trim()

      const colLines: string[][] = [
        [],
        [],
        [],
        [],
        [],
        [],
        [],
      ]

      const baseValues = [
        formatSwimmerNumber(s.swimmerNumber),
        `${s.last_name}, ${s.first_name}`,
        s.gender || "",
        s.leagueAge != null ? String(s.leagueAge) : "",
        String(eventsCount),
      ]

      for (let i = 0; i < baseValues.length; i++) {
        const maxTextWidth = colWidths[i] - 4
        const textLines = doc.splitTextToSize(baseValues[i], maxTextWidth)
        colLines[i] = textLines
      }

      if (comments) {
        const maxTextWidth = colWidths[5] - 4
        colLines[5] = doc.splitTextToSize(comments, maxTextWidth)
      }

      if (auditLines) {
        const maxTextWidth = colWidths[6] - 4
        const lastUpdatedParts: string[] = [auditLines.whenLine]
        if (auditLines.nameLine) lastUpdatedParts.push(auditLines.nameLine)
        if (auditLines.emailRoleLine)
          lastUpdatedParts.push(auditLines.emailRoleLine)

        lastUpdatedParts.forEach((part) => {
          const lines = doc.splitTextToSize(part, maxTextWidth)
          colLines[6].push(...lines)
        })
      }

      const lineCounts = colLines.map((lines) => (lines.length > 0 ? lines.length : 1))
      const maxLines = Math.max(...lineCounts)
      const rowHeight = cellPaddingY * 2 + maxLines * lineHeight

      if (rowY + rowHeight > maxY) {
        doc.addPage()
        rowY = 10
        drawSectionHeader(currentSectionLabel)
        drawTableHeader()
      }

      // Draw row grid
      doc.rect(tableLeft, rowY, tableWidth, rowHeight)
      for (let i = 1; i < colBorders.length; i++) {
        doc.line(colBorders[i], rowY, colBorders[i], rowY + rowHeight)
      }

      // Draw cell text
      for (let col = 0; col < headerLabels.length; col++) {
        const lines = colLines[col]
        if (lines.length === 0) continue

        let textY = rowY + cellPaddingY + lineHeight

        if (col === 6) {
          // Slightly de-emphasize Last Updated metadata
          doc.setFontSize(bodyFontSize - 1)
          doc.setTextColor(120)
        } else {
          doc.setFontSize(bodyFontSize)
          doc.setTextColor(0)
        }

        lines.forEach((text) => {
          doc.text(text, colBorders[col] + 2, textY)
          textY += lineHeight
        })
      }

      // Reset text style
      doc.setFontSize(bodyFontSize)
      doc.setTextColor(0)

      rowY += rowHeight
    })
  })

  return doc
}

export const downloadDeclarationsPdf = (
  swimmers: IDeclarationsReportSwimmer[],
  options: {
    meetName: string
    dateLabel: string
    clubLabel?: string
    courseLabel: string
  },
) => {
  const doc = buildDeclarationsPdf(swimmers, options)
  doc.save("meet_declarations_report.pdf")
}

const getAgeGroupSortKey = (ageGroup: string | null | undefined): number => {
  if (!ageGroup || typeof ageGroup !== "string") return Number.MAX_SAFE_INTEGER
  const match = ageGroup.match(/\d+/)
  if (!match) return Number.MAX_SAFE_INTEGER
  return parseInt(match[0], 10)
}

export const buildRelayAvailabilityPdf = (
  swimmers: IDeclarationsReportSwimmer[],
  options: {
    meetName: string
    dateLabel: string
    clubLabel?: string
    courseLabel: string
    selectedAgeGroups: string[]
    genderFilter: RelayGenderFilter
  },
) => {
  const { meetName, dateLabel, clubLabel, courseLabel, selectedAgeGroups, genderFilter } =
    options

  const doc = createLetterPdf()

  doc.setFontSize(12)
  let y = 10
  doc.text(`Meet Name: ${meetName}`, 10, y)
  y += 6
  doc.text(`Meet Date: ${dateLabel}`, 10, y)
  y += 6
  if (clubLabel) {
    doc.text(`Club: ${clubLabel}`, 10, y)
    y += 6
  }
  doc.text(`Course: ${courseLabel}`, 10, y)
  y += 6
  doc.text(`Generated At: ${moment().format("YYYY-MM-DD HH:mm")}`, 10, y)
  y += 10

  doc.setFontSize(12)
  doc.text("RELAY AVAILABILITY - ATTENDING", 10, y)

  const baseFontName: string =
    ((doc as any).getFont && (doc as any).getFont().fontName) || "helvetica"

  const headerLabels = [
    "ID",
    "Swimmer Name",
    "Age",
    "Gender",
    "Relay Availability",
    "Comments",
  ]

  const pageWidth = doc.internal.pageSize.getWidth()
  const rightMargin = 10
  const tableLeft = 10
  const tableRight = pageWidth - rightMargin
  const tableWidth = tableRight - tableLeft

  const colRelative = [0.08, 0.2, 0.06, 0.08, 0.33, 0.25]
  const colWidths = colRelative.map((r) => tableWidth * r)

  const colBorders: number[] = [tableLeft]
  for (let i = 0; i < colWidths.length; i++) {
    colBorders.push(colBorders[i] + colWidths[i])
  }

  const bodyFontSize = 8
  const lineHeight = 4
  const cellPaddingY = 2
  const maxY = 280

  const startY = y + 10
  let rowY = startY

  const drawSectionHeader = (label: string) => {
    doc.setFontSize(10)
    doc.text(label, 10, rowY)
    rowY += 5
  }

  const drawTableHeader = () => {
    doc.setFontSize(bodyFontSize)
    const headerTop = rowY
    const headerHeight = cellPaddingY * 2 + lineHeight

    headerLabels.forEach((label, idx) => {
      const maxTextWidth = colWidths[idx] - 4
      const textLines = doc.splitTextToSize(label, maxTextWidth)
      let textY = headerTop + cellPaddingY + lineHeight
      textLines.forEach((text: string) => {
        doc.text(text, colBorders[idx] + 2, textY)
        textY += lineHeight
      })
    })

    doc.rect(tableLeft, headerTop, tableWidth, headerHeight)
    for (let i = 1; i < colBorders.length; i++) {
      doc.line(colBorders[i], headerTop, colBorders[i], headerTop + headerHeight)
    }

    rowY += headerHeight
  }

  const selectedAgeSet = new Set(selectedAgeGroups || [])

  const attendingSwimmers = swimmers.filter(
    (s) => deriveDeclarationStatus(s) === "attending",
  )

  type GroupKey = {
    ageGroup: string
    eventGender: "Boys" | "Girls" | "Mixed"
  }

  const genderOrder: Record<"Girls" | "Boys" | "Mixed", number> = {
    Girls: 0,
    Boys: 1,
    Mixed: 2,
  }

  const groupsMap = new Map<string, GroupKey>()

  attendingSwimmers.forEach((s) => {
    s.elligibleEvents?.forEach((evt) => {
      if (evt.isRelay !== 1) return
      if (!evt.age_group || !evt.eventGender) return

      if (selectedAgeSet.size > 0 && !selectedAgeSet.has(evt.age_group)) return

      if (genderFilter === "girls" && evt.eventGender !== "Girls") return
      if (genderFilter === "boys" && evt.eventGender !== "Boys") return

      // Only include events where a parent explicitly marked availability
      if (evt.relayAvailable !== 0 && evt.relayAvailable !== 1) return

      const key = `${evt.age_group}__${evt.eventGender}`
      if (!groupsMap.has(key)) {
        groupsMap.set(key, {
          ageGroup: evt.age_group,
          eventGender: evt.eventGender,
        })
      }
    })
  })

  const groups = Array.from(groupsMap.values())
  groups.sort((a, b) => {
    const ageKeyA = getAgeGroupSortKey(a.ageGroup)
    const ageKeyB = getAgeGroupSortKey(b.ageGroup)
    if (ageKeyA !== ageKeyB) return ageKeyA - ageKeyB
    const genderRankA = genderOrder[a.eventGender]
    const genderRankB = genderOrder[b.eventGender]
    if (genderRankA !== genderRankB) return genderRankA - genderRankB
    return a.ageGroup.localeCompare(b.ageGroup)
  })

  if (groups.length === 0) {
    rowY += 4
    doc.setFontSize(10)
    doc.text(
      "No attending swimmers with relay availability for the selected filters.",
      10,
      rowY,
    )
    return doc
  }

  const relayColIndex = headerLabels.indexOf("Relay Availability")

  groups.forEach((group, index) => {
    const sectionLabel = `${group.ageGroup.toUpperCase()} ${group.eventGender.toUpperCase()}`

    const swimmersInSection = attendingSwimmers.filter((s) => {
      const relayEvents =
        s.elligibleEvents?.filter((evt) => {
          if (evt.isRelay !== 1) return false
          if (!evt.age_group || !evt.eventGender) return false
          if (evt.age_group !== group.ageGroup) return false
          if (evt.eventGender !== group.eventGender) return false
          if (selectedAgeSet.size > 0 && !selectedAgeSet.has(evt.age_group))
            return false
          if (genderFilter === "girls" && evt.eventGender !== "Girls")
            return false
          if (genderFilter === "boys" && evt.eventGender !== "Boys")
            return false
          return evt.relayAvailable === 0 || evt.relayAvailable === 1
        }) || []

      return relayEvents.length > 0
    })

    if (swimmersInSection.length === 0) return

    swimmersInSection.sort((a, b) => compareDeclarationsByStatusThenName(a, b))

    if (index > 0) {
      rowY += 4
    }

    if (rowY + 15 > maxY) {
      doc.addPage()
      rowY = 10
    }

    drawSectionHeader(sectionLabel)
    drawTableHeader()

    swimmersInSection.forEach((s) => {
      const relayEvents =
        s.elligibleEvents?.filter((evt) => {
          if (evt.isRelay !== 1) return false
          if (!evt.age_group || !evt.eventGender) return false
          if (evt.age_group !== group.ageGroup) return false
          if (evt.eventGender !== group.eventGender) return false
          if (selectedAgeSet.size > 0 && !selectedAgeSet.has(evt.age_group))
            return false
          if (genderFilter === "girls" && evt.eventGender !== "Girls")
            return false
          if (genderFilter === "boys" && evt.eventGender !== "Boys")
            return false
          return evt.relayAvailable === 0 || evt.relayAvailable === 1
        }) || []

      if (relayEvents.length === 0) return

      const comments = (s.comments || "").trim()

      const colLines: string[][] = [[], [], [], [], [], []]

      const baseValues = [
        formatSwimmerNumber(s.swimmerNumber),
        `${s.last_name}, ${s.first_name}`,
        s.leagueAge != null ? String(s.leagueAge) : "",
        s.gender || "",
      ]

      for (let i = 0; i < baseValues.length; i++) {
        const maxTextWidth = colWidths[i] - 4
        const textLines = doc.splitTextToSize(baseValues[i], maxTextWidth)
        colLines[i] = textLines
      }

      // Relay Availability column
      relayEvents.forEach((evt) => {
        const maxTextWidth = colWidths[relayColIndex] - 4

        const eventNumberLine = `#${evt.eventNumber}`
        const detailLine = `${evt.age_group} ${evt.eventGender} ${evt.distance} ${evt.stroke}:`
        const statusLine =
          evt.relayAvailable === 1 ? "Available" : "Not Available"

        const eventNumberLines = doc.splitTextToSize(
          eventNumberLine,
          maxTextWidth,
        )
        const detailLines = doc.splitTextToSize(detailLine, maxTextWidth)
        const statusLines = doc.splitTextToSize(statusLine, maxTextWidth)

        colLines[relayColIndex].push(...eventNumberLines)
        colLines[relayColIndex].push(...detailLines)
        colLines[relayColIndex].push(...statusLines)
      })

      // Comments column
      if (comments) {
        const maxTextWidth = colWidths[5] - 4
        colLines[5] = doc.splitTextToSize(comments, maxTextWidth)
      }

      const lineCounts = colLines.map((lines) =>
        lines.length > 0 ? lines.length : 1,
      )
      const maxLines = Math.max(...lineCounts)
      const rowHeight = cellPaddingY * 2 + maxLines * lineHeight

      if (rowY + rowHeight > maxY) {
        doc.addPage()
        rowY = 10
        drawSectionHeader(sectionLabel)
        drawTableHeader()
      }

      doc.rect(tableLeft, rowY, tableWidth, rowHeight)
      for (let i = 1; i < colBorders.length; i++) {
        doc.line(colBorders[i], rowY, colBorders[i], rowY + rowHeight)
      }

      for (let col = 0; col < headerLabels.length; col++) {
        const lines = colLines[col]
        if (lines.length === 0) continue

        let textY = rowY + cellPaddingY + lineHeight

        doc.setFontSize(bodyFontSize)
        doc.setTextColor(0)

        lines.forEach((text) => {
          const trimmed = text.trim()
          if (
            col === relayColIndex &&
            (trimmed === "Available" || trimmed === "Not Available")
          ) {
            ;(doc as any).setFont(baseFontName, "bold")
          } else {
            ;(doc as any).setFont(baseFontName, "normal")
          }
          doc.text(text, colBorders[col] + 2, textY)
          textY += lineHeight
        })

        ;(doc as any).setFont(baseFontName, "normal")
      }

      rowY += rowHeight
    })
  })

  return doc
}

export const downloadRelayAvailabilityPdf = (
  swimmers: IDeclarationsReportSwimmer[],
  options: {
    meetName: string
    dateLabel: string
    clubLabel?: string
    courseLabel: string
    selectedAgeGroups: string[]
    genderFilter: RelayGenderFilter
  },
) => {
  const doc = buildRelayAvailabilityPdf(swimmers, options)
  doc.save("relay_availability_report.pdf")
}
